/**
 * Reader-local helpers — every item here is something the store does not provide (reported to
 * the coordinator as a store gap rather than added to `$lib`):
 *
 * - body cache read/write + 140-char snippet write-back (`db.bodies`, `db.messages`, `touchBody`);
 * - a flags write-back after a label change (the store's `setLabel` posts the action but does not
 *   patch the cached row);
 * - attachment classification (inline parts → cid map, previewable types, named parts);
 * - the `.eml` preview dialog: link detection, URL parsing, `srcdoc` builder and the handful of
 *   strings that have no i18n key (the legacy `ui.js` carried them as literals).
 */
import { api } from '$lib/api/client';
import type { ApiAttachment, MessageDetail } from '$lib/api/types';
import { db, rowKey } from '$lib/db';
import { touchBody } from '$lib/db/policy';
import type { Lang } from '$lib/i18n';
import { snippetOf, type InlineAttachment } from '$lib/mail/sanitize';
import { store } from '$lib/store';
import type { BodyRec, MessageRow } from '$lib/types';

/** What the attachment viewer shows (spec §10 "Viewer"). */
export interface ViewerSpec {
  url: string;
  dlUrl: string;
  type: string;
  name: string;
}

/** Items of the More menu that the reader acts on (the two links handle themselves). */
export type MenuAction =
  | 'replyAll'
  | 'forward'
  | 'star'
  | 'markUnread'
  | 'move'
  | 'junk'
  | 'editAsNew'
  | 'addSenderContact'
  | 'fromSender'
  | 'muteThread'
  | 'print';

/** Skeleton bar widths (%); `0` is a 12 px gap (spec §10). */
export const SKELETON_BARS: readonly number[] = Object.freeze([90, 100, 96, 60, 0, 88, 94, 40]);

export interface CachedBody {
  type: 'text/plain' | 'text/html';
  content: string;
  attachments: ApiAttachment[];
  fromAttached: boolean;
}

/** Legacy `bodies.meta` is a JSON string `{attachments, fromAttached?}`. */
export function parseBodyMeta(meta: string | null | undefined): { attachments: ApiAttachment[]; fromAttached: boolean } {
  try {
    const m = JSON.parse(meta || '{}') as { attachments?: unknown; fromAttached?: unknown };
    return { attachments: Array.isArray(m.attachments) ? (m.attachments as ApiAttachment[]) : [], fromAttached: !!m.fromAttached };
  } catch {
    return { attachments: [], fromAttached: false };
  }
}

/** Cached row (from `store.msgs`, else IndexedDB) and cached body; touches the body's LRU clock. */
export async function readCached(folder: string, uid: number): Promise<{ row: MessageRow | null; body: CachedBody | null }> {
  const key = rowKey(folder, uid);
  let row: MessageRow | null = store.msgs.find((m) => m.uid === uid && m.folder === folder) ?? null;
  if (!row) row = (await db.messages.get(key).catch(() => undefined)) ?? null;
  const rec = await db.bodies.get(key).catch(() => undefined);
  if (!rec) return { row, body: null };
  void touchBody(key);
  return { row, body: { type: rec.type, content: rec.content, ...parseBodyMeta(rec.meta) } };
}

/** Persist the fetched body (legacy `bodies` shape) and write the list snippet back to the row. */
export async function cacheBody(folder: string, uid: number, m: MessageDetail): Promise<void> {
  if (!m.body) return;
  const key = rowKey(folder, uid);
  const rec: BodyRec = {
    key,
    type: m.body.type,
    content: m.body.content,
    meta: JSON.stringify({ attachments: m.attachments ?? [], fromAttached: !!m.body.fromAttached }),
    fetchedAt: Date.now(),
  };
  await db.bodies.put(rec).catch(() => undefined);
  const snippet = snippetOf(m.body);
  if (snippet) await writeSnippet(folder, uid, snippet);
}

/** Snippet write-back: the in-memory row (so the list updates) and the cached row. */
export async function writeSnippet(folder: string, uid: number, snippet: string): Promise<void> {
  const key = rowKey(folder, uid);
  const row = store.msgs.find((m) => m.key === key);
  if (row && row.snippet !== snippet) row.snippet = snippet;
  await db.messages.update(key, { snippet }).catch(() => undefined);
}

/** Flags write-back after a label change (the row in memory + the cached row). */
export async function writeFlags(folder: string, uid: number, flags: readonly string[]): Promise<void> {
  const key = rowKey(folder, uid);
  const row = store.msgs.find((m) => m.key === key);
  if (row) row.flags = [...flags];
  await db.messages.update(key, { flags: [...flags] }).catch(() => undefined);
}

/** `cid:` map for the sanitiser: every part that carries a Content-ID. */
export function inlineParts(folder: string, uid: number, atts: readonly ApiAttachment[] | null | undefined): InlineAttachment[] {
  return (atts ?? []).filter((a) => !!a.cid).map((a) => ({ cid: a.cid as string, url: api.attachmentUrl(folder, uid, a.part) }));
}

/** Footer chips: non-inline parts with a filename. */
export function namedAttachments(atts: readonly ApiAttachment[] | null | undefined): ApiAttachment[] {
  return (atts ?? []).filter((a) => !a.inline && !!a.filename);
}

/** `image/*` and PDF open in the viewer; everything else downloads. */
export function isPreviewable(type: string | null | undefined): boolean {
  const t = (type || '').toLowerCase();
  return t.startsWith('image/') || t === 'application/pdf';
}

// ---- attached-message (.eml) preview -----------------------------------------------------------

/** Capture-phase filter: an attachment link whose text or `download` name ends in `.eml`. */
export function isEmlAnchor(a: HTMLAnchorElement): boolean {
  const href = a.getAttribute('href') || '';
  if (!href.includes('/api/attachment/')) return false;
  if (a.closest('.tik-eml')) return false;
  const dl = (a.getAttribute('download') || '').trim().toLowerCase();
  const text = (a.textContent || '').trim().toLowerCase();
  return dl.endsWith('.eml') || text.endsWith('.eml');
}

/** `/api/attachment/:uid/:part?folder=…` → its parts, or null. */
export function parseAttachmentHref(href: string): { folder: string; uid: number; part: string } | null {
  try {
    const u = new URL(href, typeof location !== 'undefined' ? location.href : 'http://localhost/');
    const m = /^\/api\/attachment\/(\d+)\/([^/]+)$/.exec(u.pathname);
    const folder = u.searchParams.get('folder') || '';
    if (!m || !folder) return null;
    return { folder, uid: Number(m[1]), part: decodeURIComponent(m[2] as string) };
  } catch {
    return null;
  }
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Strings the legacy `ui.js` carried as literals (no dictionary key exists for them). */
export function emlStrings(lang: Lang): { close: string; bodyTitle: string; failed: string; failedBody: string } {
  return lang === 'ar'
    ? {
        close: 'إغلاق',
        bodyTitle: 'محتوى الرسالة المرفقة',
        failed: 'تعذّر الاستعراض',
        failedBody: 'لم يُمكن تحليل هذه الرسالة المرفقة. يمكنك تنزيلها من الزر أدناه.',
      }
    : {
        close: 'Close',
        bodyTitle: 'Attached message content',
        failed: 'Preview failed',
        failedBody: 'This attached message could not be parsed. You can download it below.',
      };
}

export const EML_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'";
export const EML_STYLE =
  'body{margin:14px;font:14px/1.75 system-ui,sans-serif;color:#262d3c;overflow-wrap:anywhere} img{max-width:100%} blockquote{border-inline-start:3px solid #cbd1da;margin-inline:0;padding-inline-start:12px;color:#5a6372} a{color:#005ad1}';

/** Sandboxed `srcdoc` for the attached message: html as-is (CSP-gated) or escaped text. */
export function emlSrcdoc(html: string | null | undefined, text: string | null | undefined): string {
  const content = html || `<pre style="white-space:pre-wrap;margin:0">${escapeHtml(text || '')}</pre>`;
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${EML_CSP}"><style>${EML_STYLE}</style></head><body dir="auto">${content}</body></html>`;
}
